import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box, Paper, Typography, CircularProgress, Card, CardContent, Button, Divider, Chip, Avatar
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {
    Person, DriveEta, Phone, AccessTime, AttachMoney, LocationOn, SupportAgent, Cancel
} from '@mui/icons-material';
import { getRide } from '../services/rideService';

// Robust interface matching the backend response structure
interface RideDetailType {
    id: number;
    pickupLocation: string;
    dropoffLocation: string;
    distanceMeters?: number | null;
    durationSeconds?: number | null;
    status: string;
    paymentStatus?: string | null;
    paymentId?: string;
    totalFare?: number | null;
    baseFare?: number | null;
    tip?: number | null;
    riderSubsidyApplied?: number | null;
    requestedAt: string;
    startedAt?: string;
    completedAt?: string;
    cancelledAt?: string | null;
    cancelledBy?: string | null; // 'rider' | 'driver' | 'system'
    cancellationReason?: string | null;
    expiredAt?: string | null;
    riderId: string;
    driverId?: number;

    // Nested Rider Object (User)
    rider?: {
        firebaseUid: string;
        name?: string;
        phone?: string;
        email?: string;
    };

    // Nested Driver Object
    driver?: {
        id: number;
        userUid: string;
        name?: string;
        phone?: string;
        email?: string;
    };
}

const RideDetail: React.FC = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [ride, setRide] = useState<RideDetailType | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        const fetchRide = async () => {
            if (!id) return;
            try {
                const res = await getRide(Number(id));
                setRide(res.data as unknown as RideDetailType);
            } catch (err) {
                setError('Failed to fetch ride details');
            } finally {
                setLoading(false);
            }
        };
        fetchRide();
    }, [id]);

    if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 5 }}><CircularProgress /></Box>;
    if (error || !ride) return <Box sx={{ p: 3 }}><Typography color="error">{error || 'Ride not found'}</Typography></Box>;

    const InfoRow = ({ label, value, icon }: { label: string, value: any, icon?: React.ReactNode }) => (
        <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                {icon}
                <Typography variant="subtitle2" color="textSecondary" sx={{ fontWeight: 500 }}>
                    {label}
                </Typography>
            </Box>
            <Typography variant="body1" component="div" sx={{ fontWeight: 400 }}>
                {value || <span style={{ color: '#aaa' }}>-</span>}
            </Typography>
        </Box>
    );

    return (
        <Box sx={{ p: 3, maxWidth: 1400, margin: '0 auto' }}>
            {/* Header Navigation */}
            <Button
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate('/rides')}
                sx={{ mb: 2, textTransform: 'none' }}
            >
                Back to Rides
            </Button>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                    Ride Details <Chip label={`#${ride.id}`} size="small" sx={{ ml: 1, verticalAlign: 'middle' }} />
                </Typography>
                <Chip
                    label={ride.status}
                    color={['completed', 'paid'].includes(ride.status) ? 'success' : ride.status === 'cancelled' ? 'error' : 'primary'}
                    variant="filled"
                    sx={{ textTransform: 'uppercase', fontWeight: 'bold' }}
                />
            </Box>

            {/* Main Layout - 66% / 33% Split */}
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>

                {/* LEFT COLUMN: Main Info (66%) */}
                <Box sx={{ flex: { md: 2 }, minWidth: 0 }}>

                    {/* 1. Trip Information */}
                    <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
                        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <LocationOn color="primary" />
                            Trip Information
                        </Typography>
                        <Divider sx={{ mb: 3 }} />

                        {/* 2-column Grid */}
                        <Box sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                            gap: 4
                        }}>
                            <Box>
                                <InfoRow label="Pickup Location" value={ride.pickupLocation} icon={<LocationOn fontSize="small" color="error"/>} />
                                <InfoRow label="Dropoff Location" value={ride.dropoffLocation} icon={<LocationOn fontSize="small" color="success"/>} />
                                <InfoRow label="Distance" value={`${((ride.distanceMeters ?? 0) / 1000).toFixed(2)} km`} icon={<DriveEta fontSize="small" color="action"/>} />
                                <InfoRow label="Duration" value={`${Math.floor((ride.durationSeconds ?? 0) / 60)} mins`} icon={<AccessTime fontSize="small" color="action"/>} />
                            </Box>
                            <Box>
                                <InfoRow label="Requested At" value={new Date(ride.requestedAt).toLocaleString()} icon={<AccessTime fontSize="small" color="action"/>} />
                                <InfoRow label="Started At" value={ride.startedAt ? new Date(ride.startedAt).toLocaleString() : '-'} icon={<AccessTime fontSize="small" color="action"/>} />
                                <InfoRow label="Completed At" value={ride.completedAt ? new Date(ride.completedAt).toLocaleString() : '-'} icon={<AccessTime fontSize="small" color="action"/>} />
                            </Box>
                        </Box>
                    </Paper>

                    {/* 1b. Cancellation (only when cancelled / expired) */}
                    {(ride.status === 'cancelled' || ride.status === 'expired') && (() => {
                        const by = ride.cancelledBy;
                        const whoCancelled =
                            by === 'rider' ? `Rider — ${ride.rider?.name || ride.riderId}`
                            : by === 'driver' ? `Driver — ${ride.driver?.name || ride.driverId || ''}`
                            : by === 'system' ? 'System (timed out — no driver claimed it)'
                            : 'Unknown (cancelled before this was recorded)';
                        const chipColor: 'info' | 'warning' | 'default' =
                            by === 'rider' ? 'info' : by === 'driver' ? 'warning' : 'default';
                        return (
                            <Paper sx={{ p: 3, mb: 3, borderRadius: 2, borderLeft: '4px solid #d32f2f' }}>
                                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Cancel color="error" />
                                    {ride.status === 'expired' ? 'Expiry' : 'Cancellation'}
                                </Typography>
                                <Divider sx={{ mb: 3 }} />
                                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 4 }}>
                                    <Box>
                                        <InfoRow
                                            label="Cancelled By"
                                            value={<Chip size="small" label={whoCancelled} color={chipColor} sx={{ height: 'auto', '& .MuiChip-label': { whiteSpace: 'normal', py: 0.5 } }} />}
                                            icon={<Cancel fontSize="small" color="error" />}
                                        />
                                        <InfoRow label="Reason" value={ride.cancellationReason} />
                                    </Box>
                                    <Box>
                                        <InfoRow
                                            label={ride.status === 'expired' ? 'Expired At' : 'Cancelled At'}
                                            value={(ride.cancelledAt || ride.expiredAt) ? new Date((ride.cancelledAt || ride.expiredAt) as string).toLocaleString() : '-'}
                                            icon={<AccessTime fontSize="small" color="action" />}
                                        />
                                    </Box>
                                </Box>
                            </Paper>
                        );
                    })()}

                    {/* 2. Fare Details */}
                    <Paper sx={{ p: 3, borderRadius: 2 }}>
                        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <AttachMoney color="success" />
                            Payment & Fare Breakdown
                        </Typography>
                        <Divider sx={{ mb: 3 }} />
                        <Box sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                            gap: 4
                        }}>
                            <Box>
                                {/* Fare Breakdown */}
                                <InfoRow label="Base Fare" value={`₹${ride.baseFare ?? 0}`} />
                                <InfoRow label="Distance Fare" value={`₹${((ride.totalFare ?? 0) - (ride.baseFare ?? 0) - (ride.tip ?? 0) + (ride.riderSubsidyApplied ?? 0)).toFixed(2)}`} />
                                <InfoRow label="Tip" value={`₹${ride.tip ?? 0}`} />

                                {/* Subsidy Highlight */}
                                <InfoRow
                                    label="Less: Platform Subsidy"
                                    value={
                                        <span style={{ color: ride.riderSubsidyApplied ? 'red' : 'inherit', fontWeight: 'bold' }}>
                                            {ride.riderSubsidyApplied ? `-₹${ride.riderSubsidyApplied}` : "₹0"}
                                        </span>
                                    }
                                />

                                <Divider sx={{ my: 1 }} />
                                <InfoRow label="Total Customer Pay" value={<span style={{ fontWeight: 'bold', fontSize: '1.1em' }}>{`₹${ride.totalFare ?? 0}`}</span>} />
                            </Box>
                            <Box>
                                <InfoRow label="Payment Status" value={
                                    <Chip
                                        label={ride.paymentStatus || "PENDING"}
                                        color={String(ride.paymentStatus || '').toLowerCase() === 'paid' ? 'success' : 'warning'}
                                        size="small"
                                    />
                                } />
                                <InfoRow label="Payment ID" value={ride.paymentId} />
                            </Box>
                        </Box>
                    </Paper>
                </Box>

                {/* RIGHT COLUMN: Sidebar (33%) */}
                <Box sx={{ flex: { md: 1 }, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>

                    {/* Rider Card */}
                    <Card variant="outlined" sx={{ borderRadius: 2, borderTop: '4px solid #1976d2' }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <Person color="primary" />
                                <Typography variant="h6">Rider</Typography>
                            </Box>
                            <Divider sx={{ mb: 2 }} />

                            {ride.rider ? (
                                <>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                                        <Avatar sx={{ bgcolor: '#1976d2' }}>
                                            {ride.rider.name ? ride.rider.name.charAt(0).toUpperCase() : 'R'}
                                        </Avatar>
                                        <Box>
                                            <Typography variant="subtitle1" fontWeight="bold">
                                                {ride.rider.name || "Unknown"}
                                            </Typography>
                                            <Typography variant="caption" color="textSecondary">
                                                ID: {ride.riderId}
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <InfoRow label="Phone" value={ride.rider.phone} icon={<Phone fontSize="small" color="action"/>} />
                                </>
                            ) : (
                                <InfoRow label="Rider ID" value={ride.riderId} />
                            )}

                            <Button
                                fullWidth
                                variant="outlined"
                                onClick={() => navigate(`/users?search=${ride.riderId}`)}
                                sx={{ mt: 1 }}
                            >
                                View Rider Profile
                            </Button>
                        </CardContent>
                    </Card>

                    {/* Driver Card */}
                    <Card variant="outlined" sx={{ borderRadius: 2, borderTop: '4px solid #ed6c02' }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <DriveEta color="warning" />
                                <Typography variant="h6">Driver</Typography>
                            </Box>
                            <Divider sx={{ mb: 2 }} />

                            {ride.driverId ? (
                                <>
                                    {/* Attempt to show Driver Name/Phone if nested user object exists */}
                                    {ride.driver? (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                                            <Avatar sx={{ bgcolor: '#ed6c02' }}>
                                                {ride.driver.name ? ride.driver.name.charAt(0).toUpperCase() : 'D'}
                                            </Avatar>
                                            <Box>
                                                <Typography variant="subtitle1" fontWeight="bold">
                                                    {ride.driver.name || "Unknown"}
                                                </Typography>
                                                <Typography variant="caption" color="textSecondary">
                                                    ID: {ride.driverId}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    ) : (
                                        <Typography variant="body2" color="textSecondary" paragraph>
                                            Driver ID: {ride.driverId}
                                        </Typography>
                                    )}

                                    {ride.driver?.phone && (
                                        <InfoRow label="Phone" value={ride.driver.phone} icon={<Phone fontSize="small" color="action"/>} />
                                    )}

                                    <Button
                                        fullWidth
                                        variant="outlined"
                                        color="warning"
                                        onClick={() => navigate(`/drivers?search=${ride.driverId}`)}
                                        sx={{ mt: 1 }}
                                    >
                                        View Driver Profile
                                    </Button>
                                </>
                            ) : (
                                <Typography color="textSecondary" sx={{ fontStyle: 'italic' }}>
                                    No driver assigned.
                                </Typography>
                            )}
                        </CardContent>
                    </Card>

                    {/* Support Card */}
                    <Card variant="outlined" sx={{ borderRadius: 2 }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <SupportAgent color="secondary" />
                                <Typography variant="h6">Support</Typography>
                            </Box>
                            <Divider sx={{ mb: 2 }} />
                            <Typography variant="body2" paragraph color="textSecondary">
                                Check for any tickets raised for this ride.
                            </Typography>
                            <Button
                                fullWidth
                                variant="contained"
                                color="secondary"
                                onClick={() => navigate(`/support-tickets?rideId=${ride.id}`)}
                            >
                                Check Support Tickets
                            </Button>
                        </CardContent>
                    </Card>
                </Box>
            </Box>
        </Box>
    );
};

export default RideDetail;